import React, {useEffect} from 'react';
import {observer} from 'mobx-react';
import PhoneIcon from '@mui/icons-material/Phone';
import CallEndIcon from '@mui/icons-material/CallEnd';

import {useStores} from '../../Hooks';
import DynamicAvatar from '../Shared/DynamicAvatar';
import {startRingingTone, stopRingingTone} from '../../Utilities/RingingTone';

const TOAST_WIDTH = 332;
const TOAST_HEIGHT = 70;
const AVATAR_SIZE = 56;
const BUTTON_SIZE = 38;

const containerStyle = {
    position: 'fixed',
    bottom: 24,
    left: '50%',
    transform: 'translateX(-50%)',
    width: TOAST_WIDTH,
    height: TOAST_HEIGHT,
    padding: 16,
    backgroundColor: 'var(--background-normal-color)',
    color: 'var(--windowsandmenus-normal-text-color)',
    borderRadius: 10,
    boxShadow: '0px 9px 23.3px rgba(0, 0, 0, 0.25)',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    zIndex: 1500,
};

const avatarStyle = {
    width: AVATAR_SIZE,
    height: AVATAR_SIZE,
    fontSize: 16,
    fontWeight: 700,
    flexShrink: 0,
};

const textColumnStyle = {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    overflow: 'hidden',
};

const labelStyle = {
    fontSize: 12,
    fontWeight: 400,
    lineHeight: 1.36,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
};

const nameStyle = {
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 1.36,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
};

const buttonBaseStyle = {
    width: BUTTON_SIZE,
    height: BUTTON_SIZE,
    borderRadius: 10,
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#FFFFFF',
    flexShrink: 0,
    padding: 0,
};

const countActiveParticipants = (room) => {
    if (!room?.ParticipantsMap) return 0;
    const entries = room.ParticipantsMap instanceof Map
        ? Array.from(room.ParticipantsMap.values())
        : Object.values(room.ParticipantsMap);
    return entries.length;
};

const buildCallerUser = (caller) => ({
    _id: caller?._id,
    Name: caller?.Name,
    LastName: caller?.LastName,
    AvatarBackground: caller?.AvatarBackground,
    Email: caller?.Email,
});

const IncomingCallToast = observer(() => {
    const {incomingCallStore, generalStore} = useStores();
    const call = incomingCallStore.currentCall;

    useEffect(() => {
        if (!call) return undefined;
        startRingingTone();
        return () => stopRingingTone();
    }, [call]);

    if (!call) return null;

    const {caller, room, activeParticipantCount} = call;
    const participantCount = typeof activeParticipantCount === 'number'
        ? activeParticipantCount
        : countActiveParticipants(room);
    const isGroup = participantCount >= 2;
    const labelKey = isGroup ? 'incomingGroupCall' : 'incomingCall';
    const label = generalStore.translate(labelKey) || (isGroup ? 'Incoming group call...' : 'Incoming call...');
    const fullName = [caller?.Name, caller?.LastName].filter(Boolean).join(' ').trim();

    return (
        <div style={containerStyle} data-testid="incoming-call-toast">
            <DynamicAvatar
                user={buildCallerUser(caller)}
                imageSrc={caller?.UserIcon}
                style={avatarStyle}
            />
            <div style={textColumnStyle}>
                <span style={labelStyle}>{label}</span>
                <span style={nameStyle}>{fullName}</span>
            </div>
            <button
                type="button"
                style={{...buttonBaseStyle, backgroundColor: 'var(--onlinesuccess-normal-color)'}}
                onClick={incomingCallStore.accept}
                aria-label={generalStore.translate('join') || 'Join'}
                data-testid="incoming-call-accept"
            >
                <PhoneIcon style={{fontSize: 24}}/>
            </button>
            <button
                type="button"
                style={{...buttonBaseStyle, backgroundColor: 'var(--leavedeletenew-normal-color)'}}
                onClick={incomingCallStore.decline}
                aria-label={generalStore.translate('decline') || 'Decline'}
                data-testid="incoming-call-decline"
            >
                <CallEndIcon style={{fontSize: 24}}/>
            </button>
        </div>
    );
});

export default IncomingCallToast;
